import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Inbox,
  BarChart3,
  LogOut,
  Trash2,
  Mail,
  CheckCircle2,
  Clock,
  Archive,
  X,
  Send,
  Users,
  Eye,
  TrendingUp,
  Calendar,
  Smartphone,
  Globe2,
  Search,
  Cog,
  KeyRound,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "new", label: "Nouvelle", color: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  { value: "contacted", label: "Contacté", color: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  { value: "replied", label: "Répondu", color: "bg-green-500/15 text-green-300 border-green-500/30" },
  { value: "archived", label: "Archivé", color: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
];

function statusBadge(status) {
  const s = STATUS_OPTIONS.find((x) => x.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${s.color}`}>
      {s.label}
    </span>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("demandes");
  const [pwd, setPwd] = useState(null);
  const [demandes, setDemandes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState({ open: false, subject: "", body: "" });
  const [replySending, setReplySending] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  // Auth & data load
  useEffect(() => {
    const stored = localStorage.getItem("dj_admin_pwd");
    if (!stored) {
      navigate("/admin/login");
      return;
    }
    setPwd(stored);
  }, [navigate]);

  const fetchAll = useCallback(async () => {
    if (!pwd) return;
    setLoading(true);
    try {
      const headers = { "X-Admin-Password": pwd };
      const [d, s] = await Promise.all([
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/stats?days=90`, { headers }),
      ]);
      setDemandes(d.data || []);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("dj_admin_pwd");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [pwd, navigate]);

  useEffect(() => {
    if (pwd) fetchAll();
  }, [pwd, fetchAll]);

  const logout = () => {
    localStorage.removeItem("dj_admin_pwd");
    navigate("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact/${id}`, { status }, { headers: { "X-Admin-Password": pwd } });
      setDemandes((d) => d.map((x) => (x.id === id ? { ...x, status } : x)));
      if (active?.id === id) setActive((a) => ({ ...a, status }));
    } catch {}
  };

  const deleteRequest = async (id) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    try {
      await axios.delete(`${API}/contact/${id}`, { headers: { "X-Admin-Password": pwd } });
      setDemandes((d) => d.filter((x) => x.id !== id));
      if (active?.id === id) setActive(null);
    } catch {}
  };

  const exportCsv = () => {
    const headers = ["id", "created_at", "status", "full_name", "email", "phone", "event_type", "event_date", "location", "guests", "budget", "message"];
    const lines = [headers.join(",")].concat(
      demandes.map((r) =>
        headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dj-as-demandes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openReply = (d) => {
    setReply({
      open: true,
      subject: `Re : votre demande de devis – DJ AS Animation`,
      body: `Bonjour ${d.full_name?.split(" ")[0] || ""},\n\nMerci pour votre demande concernant votre ${d.event_type?.toLowerCase()}${d.event_date ? ` du ${d.event_date}` : ""}.\n\n[Votre réponse ici]\n\nBien cordialement,\nAdrien Sebert\nDJ AS Animation`,
    });
  };

  const sendReply = async () => {
    if (!active) return;
    setReplySending(true);
    try {
      await axios.post(
        `${API}/contact/${active.id}/reply`,
        { subject: reply.subject, body: reply.body },
        { headers: { "X-Admin-Password": pwd } }
      );
      setReply({ open: false, subject: "", body: "" });
      setDemandes((d) => d.map((x) => (x.id === active.id ? { ...x, status: "replied" } : x)));
      setActive((a) => ({ ...a, status: "replied" }));
      alert("Email envoyé avec succès !");
    } catch (e) {
      alert("Erreur lors de l'envoi : " + (e?.response?.data?.detail || e.message));
    } finally {
      setReplySending(false);
    }
  };

  const filtered = demandes.filter((d) => {
    if (filter !== "all" && d.status !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (
        !(`${d.full_name} ${d.email} ${d.phone} ${d.event_type} ${d.location || ""} ${d.message || ""}`)
          .toLowerCase()
          .includes(s)
      )
        return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="px-5 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E9C766] via-[#D4AF37] to-[#8a6618] flex items-center justify-center text-black font-bold text-sm">
              AS
            </span>
            <div>
              <div className="text-sm font-semibold tracking-wide">Espace admin</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">DJ AS Animation</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-xs text-zinc-400 hover:text-white hidden sm:inline">← Voir le site</a>
            <button onClick={logout} data-testid="admin-logout" className="text-xs px-3 py-2 rounded-full border border-white/10 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] inline-flex items-center gap-1.5">
              <LogOut size={13} /> Déconnexion
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-5 md:px-10 flex gap-1 border-t border-white/5">
          {[
            { key: "demandes", label: "Demandes", icon: <Inbox size={14} /> },
            { key: "stats", label: "Statistiques", icon: <BarChart3 size={14} /> },
            { key: "settings", label: "Paramètres", icon: <Cog size={14} /> },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-3.5 text-sm font-medium inline-flex items-center gap-2 border-b-2 transition-colors ${
                tab === t.key ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-zinc-400 hover:text-white"
              }`}
            >
              {t.icon} {t.label}
              {t.key === "demandes" && demandes.filter((d) => d.status === "new").length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] bg-[#D4AF37] text-black font-bold">
                  {demandes.filter((d) => d.status === "new").length}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>

      <main className="px-5 md:px-10 py-8">
        {loading && <div className="text-zinc-500 text-sm">Chargement…</div>}

        {!loading && tab === "demandes" && (
          <DemandesTab
            demandes={filtered}
            allCount={demandes.length}
            statusCounts={STATUS_OPTIONS.map((o) => ({
              ...o,
              count: demandes.filter((d) => d.status === o.value).length,
            }))}
            filter={filter}
            setFilter={setFilter}
            search={search}
            setSearch={setSearch}
            onOpen={setActive}
            onExport={exportCsv}
          />
        )}

        {!loading && tab === "stats" && stats && <StatsTab stats={stats} />}

        {!loading && tab === "settings" && <SettingsTab pwd={pwd} />}
      </main>

      {/* Detail drawer */}
      {active && (
        <DetailDrawer
          item={active}
          onClose={() => setActive(null)}
          onStatusChange={(s) => updateStatus(active.id, s)}
          onDelete={() => deleteRequest(active.id)}
          onReply={() => openReply(active)}
        />
      )}

      {/* Reply modal */}
      {reply.open && (
        <ReplyModal
          to={active?.email}
          subject={reply.subject}
          body={reply.body}
          onSubjectChange={(v) => setReply((r) => ({ ...r, subject: v }))}
          onBodyChange={(v) => setReply((r) => ({ ...r, body: v }))}
          onClose={() => setReply({ open: false, subject: "", body: "" })}
          onSend={sendReply}
          sending={replySending}
        />
      )}
    </div>
  );
}

/* -------- Demandes Tab -------- */
function DemandesTab({ demandes, allCount, statusCounts, filter, setFilter, search, setSearch, onOpen, onExport }) {
  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Demandes de devis</h2>
          <p className="text-sm text-zinc-400 mt-1">{allCount} demande(s) au total</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="search"
              placeholder="Rechercher…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search"
              className="pl-9 pr-3 py-2 rounded-full bg-white/[0.03] border border-white/10 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37]/40 w-56"
            />
          </div>
          <button onClick={onExport} data-testid="admin-export-csv" className="text-xs px-4 py-2 rounded-full border border-white/10 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]">
            Exporter CSV
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        <button
          onClick={() => setFilter("all")}
          className={`text-xs px-4 py-2 rounded-full border ${
            filter === "all" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/10 text-zinc-400 hover:text-white"
          }`}
        >
          Toutes · {allCount}
        </button>
        {statusCounts.map((s) => (
          <button
            key={s.value}
            onClick={() => setFilter(s.value)}
            data-testid={`filter-${s.value}`}
            className={`text-xs px-4 py-2 rounded-full border ${
              filter === s.value ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            {s.label} · {s.count}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-zinc-400 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Nom</th>
              <th className="px-4 py-3 text-left">Événement</th>
              <th className="px-4 py-3 text-left hidden md:table-cell">Lieu</th>
              <th className="px-4 py-3 text-left hidden md:table-cell">Budget</th>
              <th className="px-4 py-3 text-left">Statut</th>
            </tr>
          </thead>
          <tbody>
            {demandes.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-zinc-500">
                  Aucune demande pour le moment.
                </td>
              </tr>
            )}
            {demandes.map((d) => (
              <tr
                key={d.id}
                onClick={() => onOpen(d)}
                data-testid={`row-${d.id}`}
                className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 text-xs text-zinc-400 whitespace-nowrap">
                  {d.created_at ? new Date(d.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-white">{d.full_name}</div>
                  <div className="text-xs text-zinc-500">{d.email}</div>
                </td>
                <td className="px-4 py-3 text-zinc-300">{d.event_type}</td>
                <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">{d.location || "—"}</td>
                <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">{d.budget || "—"}</td>
                <td className="px-4 py-3">{statusBadge(d.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------- Detail Drawer -------- */
function DetailDrawer({ item, onClose, onStatusChange, onDelete, onReply }) {
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="detail-drawer" className="w-full md:w-[520px] bg-[#0A0A0A] border-l border-white/10 overflow-y-auto p-6 md:p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-zinc-500">Demande</div>
            <div className="text-xl font-bold mt-1">{item.full_name}</div>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <a href={`mailto:${item.email}`} className="text-xs px-3 py-2 rounded-full border border-white/10 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]">
            ✉ {item.email}
          </a>
          <a href={`tel:${item.phone}`} className="text-xs px-3 py-2 rounded-full border border-white/10 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]">
            ☎ {item.phone}
          </a>
          <a
            href={`https://wa.me/${item.phone.replace(/\D/g, "").replace(/^0/, "33")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-3 py-2 rounded-full border border-white/10 hover:border-green-400/40 hover:text-green-400"
          >
            WhatsApp
          </a>
        </div>

        <dl className="space-y-3 text-sm">
          {[
            ["Type d'événement", item.event_type],
            ["Date", item.event_date],
            ["Lieu", item.location],
            ["Invités", item.guests],
            ["Budget", item.budget],
            ["Reçue le", item.created_at ? new Date(item.created_at).toLocaleString("fr-FR") : null],
          ].map(([k, v]) =>
            v ? (
              <div key={k} className="flex gap-3">
                <dt className="w-32 text-xs uppercase tracking-wider text-zinc-500">{k}</dt>
                <dd className="flex-1 text-white">{v}</dd>
              </div>
            ) : null
          )}
        </dl>

        {item.message && (
          <div className="mt-6">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Message</div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">
              {item.message}
            </div>
          </div>
        )}

        {item.replies?.length > 0 && (
          <div className="mt-6">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Réponses envoyées</div>
            {item.replies.map((r, i) => (
              <div key={i} className="rounded-xl border border-green-500/20 bg-green-500/5 p-4 text-sm text-zinc-200 mb-2">
                <div className="text-xs text-green-400 font-semibold mb-1">{r.subject}</div>
                <div className="text-xs text-zinc-500 mb-2">{new Date(r.sent_at).toLocaleString("fr-FR")}</div>
                <div className="whitespace-pre-wrap">{r.body}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Statut</div>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.value}
                onClick={() => onStatusChange(s.value)}
                data-testid={`status-${s.value}`}
                className={`text-xs px-3 py-2 rounded-full border ${
                  item.status === s.value ? s.color : "border-white/10 text-zinc-400 hover:text-white"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3">
          <button onClick={onReply} data-testid="btn-reply" className="btn-gold rounded-full py-3 text-sm font-semibold inline-flex items-center justify-center gap-2">
            <Mail size={15} /> Répondre par email
          </button>
          <button onClick={onDelete} data-testid="btn-delete" className="rounded-full py-3 text-sm border border-red-500/30 text-red-400 hover:bg-red-500/10 inline-flex items-center justify-center gap-2">
            <Trash2 size={15} /> Supprimer la demande
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------- Reply Modal -------- */
function ReplyModal({ to, subject, body, onSubjectChange, onBodyChange, onClose, onSend, sending }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div data-testid="reply-modal" className="relative z-10 w-full max-w-2xl bg-[#0A0A0A] rounded-3xl border border-white/10 p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-zinc-500">Envoyer une réponse à</div>
            <div className="text-lg font-semibold mt-1">{to}</div>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white"><X size={20} /></button>
        </div>

        <label className="block text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">Sujet</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          data-testid="reply-subject"
          className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37]/40 mb-4"
        />

        <label className="block text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">Message</label>
        <textarea
          rows={12}
          value={body}
          onChange={(e) => onBodyChange(e.target.value)}
          data-testid="reply-body"
          className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37]/40 font-mono text-sm"
        />

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-zinc-400 hover:text-white">Annuler</button>
          <button
            onClick={onSend}
            disabled={sending}
            data-testid="reply-send"
            className="btn-gold rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Send size={15} /> {sending ? "Envoi..." : "Envoyer la réponse"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------- Stats Tab -------- */
function StatsTab({ stats }) {
  const { totals, last_30_days, top_sources, top_paths, by_device, by_browser, by_month, by_year } = stats;

  const maxDay = Math.max(...last_30_days.map((d) => d.count), 1);

  const StatCard = ({ icon, label, value, hint }) => (
    <div className="glass rounded-2xl p-6">
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#D4AF37]/15 text-[#D4AF37] mb-4">
        {icon}
      </div>
      <div className="text-3xl font-bold text-white">{value}</div>
      <div className="text-xs uppercase tracking-[0.22em] text-zinc-400 mt-2">{label}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Statistiques du site</h2>
        <p className="text-sm text-zinc-400 mt-1">Données sur les 90 derniers jours · mises à jour en temps réel</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard icon={<Eye size={18} />} label="Visites totales" value={totals.all_time_visits.toLocaleString("fr")} />
        <StatCard icon={<TrendingUp size={18} />} label="Aujourd'hui" value={totals.visits_today} />
        <StatCard icon={<Calendar size={18} />} label="90 derniers jours" value={totals.period_visits.toLocaleString("fr")} />
        <StatCard icon={<Users size={18} />} label="Visiteurs uniques" value={totals.unique_visitors.toLocaleString("fr")} hint="basé sur IP hashée" />
        <StatCard icon={<Mail size={18} />} label="Demandes reçues" value={totals.all_time_contacts.toLocaleString("fr")} />
      </div>

      {/* Bar chart 30 days */}
      <div className="glass rounded-2xl p-6 md:p-8">
        <h3 className="text-lg font-semibold mb-1">30 derniers jours</h3>
        <p className="text-xs text-zinc-500 mb-6">Nombre de visites par jour</p>
        <div className="flex items-end gap-1 h-40">
          {last_30_days.map((d) => {
            const h = (d.count / maxDay) * 100;
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group relative">
                <div className="w-full bg-gradient-to-t from-[#D4AF37]/30 to-[#D4AF37] rounded-t" style={{ height: `${Math.max(h, 2)}%` }} />
                <div className="absolute -top-8 hidden group-hover:block text-[10px] bg-black border border-white/10 rounded px-2 py-1 whitespace-nowrap">
                  {d.date} · {d.count}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex justify-between text-[10px] text-zinc-500">
          <span>{last_30_days[0]?.date}</span>
          <span>{last_30_days[last_30_days.length - 1]?.date}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ListCard icon={<Globe2 size={16} />} title="Sources de trafic" items={top_sources} hint="D'où viennent vos visiteurs" />
        <ListCard icon={<Search size={16} />} title="Pages les plus vues" items={top_paths} hint="URLs visitées" />
        <ListCard icon={<Smartphone size={16} />} title="Appareils" items={by_device} />
        <ListCard icon={<Globe2 size={16} />} title="Navigateurs" items={by_browser} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ListCard icon={<Calendar size={16} />} title="Par mois" items={by_month.slice(-12).reverse()} keyLabel="mois" />
        <ListCard icon={<Calendar size={16} />} title="Par année" items={by_year} keyLabel="année" />
      </div>

      <div className="text-xs text-zinc-500 italic max-w-2xl">
        💡 Note : les moteurs de recherche modernes (Google, Bing…) ne transmettent plus les mots-clés exacts pour des raisons de confidentialité.
        La meilleure analyse mots-clés se fait via Google Search Console (gratuit, à connecter à djadriensebert.com).
      </div>
    </div>
  );
}

function ListCard({ icon, title, items, hint, keyLabel }) {
  const max = Math.max(...(items || []).map((i) => i.count), 1);
  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[#D4AF37]">{icon}</span>
        <h3 className="text-base font-semibold">{title}</h3>
      </div>
      {hint && <p className="text-[11px] text-zinc-500 mb-4">{hint}</p>}
      {(!items || items.length === 0) && <div className="text-sm text-zinc-500 py-6 text-center">Aucune donnée</div>}
      <div className="space-y-2.5">
        {(items || []).map((it) => (
          <div key={it.key || it.date}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-zinc-300 truncate pr-2">{it.key || it.date}</span>
              <span className="text-zinc-500 font-semibold tabular-nums">{it.count}</span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#D4AF37]/60 to-[#FF2D9C]/60 rounded-full" style={{ width: `${(it.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------- Settings Tab -------- */
function SettingsTab({ pwd }) {
  const [cfg, setCfg] = useState(null);
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [notifyTo, setNotifyTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/settings`, { headers: { "X-Admin-Password": pwd } })
      .then((r) => {
        setCfg(r.data);
        setSmtpUser(r.data.smtp_user || "");
        setNotifyTo(r.data.notify_to || "");
      })
      .catch(() => {});
  }, [pwd]);

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const payload = { notify_to: notifyTo };
      if (smtpUser) payload.smtp_user = smtpUser;
      if (smtpPassword) payload.smtp_password = smtpPassword;
      await axios.post(`${API}/admin/settings`, payload, { headers: { "X-Admin-Password": pwd } });
      const r = await axios.get(`${API}/admin/settings`, { headers: { "X-Admin-Password": pwd } });
      setCfg(r.data);
      setSmtpPassword("");
      setFeedback({ type: "ok", msg: "Paramètres sauvegardés ✓" });
    } catch (e) {
      setFeedback({ type: "err", msg: e?.response?.data?.detail || "Erreur" });
    } finally {
      setSaving(false);
    }
  };

  const testSend = async () => {
    setTesting(true);
    setFeedback(null);
    try {
      const r = await axios.post(`${API}/admin/test-email`, {}, { headers: { "X-Admin-Password": pwd } });
      setFeedback({ type: "ok", msg: `Email test envoyé à ${r.data.sent_to} ✓` });
    } catch (e) {
      setFeedback({ type: "err", msg: e?.response?.data?.detail || "Échec du test" });
    } finally {
      setTesting(false);
    }
  };

  const inputCls =
    "w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37]/40 text-sm";

  return (
    <div className="max-w-2xl">
      <h2 className="text-2xl font-bold tracking-tight">Paramètres email</h2>
      <p className="text-sm text-zinc-400 mt-1 mb-8">
        Configurez le compte Gmail utilisé pour envoyer vos demandes et réponses. Ces valeurs ont priorité sur celles du serveur.
      </p>

      <div className="glass rounded-2xl p-7 space-y-5">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
            Compte Gmail expéditeur (SMTP)
          </label>
          <input
            type="email"
            value={smtpUser}
            onChange={(e) => setSmtpUser(e.target.value)}
            placeholder="adrien.sebert@gmail.com"
            className={inputCls}
            data-testid="settings-smtp-user"
          />
          <div className="text-[11px] text-zinc-500 mt-1.5">
            Doit avoir la validation en 2 étapes activée.
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
            Mot de passe d'application Gmail
          </label>
          <input
            type="password"
            value={smtpPassword}
            onChange={(e) => setSmtpPassword(e.target.value)}
            placeholder={cfg?.smtp_password_preview || "16 caractères sans espaces"}
            className={inputCls}
            data-testid="settings-smtp-password"
          />
          <div className="text-[11px] text-zinc-500 mt-1.5">
            {cfg?.smtp_password_set ? (
              <>Actuel : <span className="text-zinc-300 font-mono">{cfg.smtp_password_preview}</span> — laissez vide pour ne pas changer.</>
            ) : (
              "Aucun mot de passe configuré."
            )}
            {" · "}
            <a
              href="https://myaccount.google.com/apppasswords"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D4AF37] hover:underline"
            >
              Générer un mot de passe d'application
            </a>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
            Adresse de réception des devis
          </label>
          <input
            type="email"
            value={notifyTo}
            onChange={(e) => setNotifyTo(e.target.value)}
            placeholder="asanimation61@gmail.com"
            className={inputCls}
            data-testid="settings-notify-to"
          />
        </div>

        {feedback && (
          <div
            data-testid="settings-feedback"
            className={`text-sm px-4 py-3 rounded-lg ${
              feedback.type === "ok"
                ? "bg-green-500/10 border border-green-500/30 text-green-300"
                : "bg-red-500/10 border border-red-500/30 text-red-300"
            }`}
          >
            {feedback.msg}
          </div>
        )}

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            onClick={save}
            disabled={saving}
            data-testid="settings-save"
            className="btn-gold rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
          >
            <KeyRound size={15} /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
          <button
            onClick={testSend}
            disabled={testing}
            data-testid="settings-test"
            className="btn-outline-gold rounded-full px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Send size={15} /> {testing ? "Envoi…" : "Envoyer un email test"}
          </button>
        </div>
      </div>

      <div className="mt-6 text-xs text-zinc-500 italic">
        💡 Astuce : après avoir collé un nouveau mot de passe, cliquez sur "Enregistrer" puis "Envoyer un email test"
        pour vérifier immédiatement que ça fonctionne.
      </div>
    </div>
  );
}
